/**
 * Customers' two answers under a cited item on a place page (place page v4 item 22; coordinator, 2026-10-16, "fix all items" with the
 * recommended default). Behind its own switch, PLACE_VOTES (services/api config.placeVotes), OFF by default: a new public write that waits for
 * a security audit before it lands toward production.
 *  - "Useful information" (yes / no): the page shows one public line per item, "N people found this useful" (yes answers only, never the
 *    no answers, never a ratio, never a star; no structured data).
 *  - "True from your visit to <Name>" (yes / no): PRIVATE. Never on the page; the business's own team and our team read it, like a guest note.
 * Signed in only (one answer per account, per item, per question: a new answer replaces the old one), and a limit per account per hour.
 * The page itself never knows who is signed in (web sessions are the app's bearer tokens): each answer is a link into the app, which signs
 * the person in and sends it (apps/web screens/PlaceVote.tsx).
 */
import { SIGNUP_BASE } from "./fixit-home.ts";
import { VOICE_ID } from "./fixvoice.ts";

export const VOTE_KINDS = ["useful", "true"] as const;
export type VoteKind = (typeof VOTE_KINDS)[number];
export const VOTE_VALUES = ["up", "down"] as const;
export type VoteValue = (typeof VOTE_VALUES)[number];
/** Answers one account may send in an hour (each a new or changed answer). */
export const VOTES_PER_ACCOUNT_HOUR = 30;
/** A count shows on the page only from this many (one person's answer is not a public fact). */
export const VOTE_SHOW_MIN = 2;

const PLACE_KEY = /^[a-z0-9][a-z0-9-]{0,39}:[A-Za-z0-9._-]{1,100}$/;
export interface VoteInput { placeKey: string; itemId: string; kind: VoteKind; value: VoteValue }
/** A vote from a query or a body, or null (every field checked; the item id is the reply id shape, core voiceIdOf). */
export function voteFrom(v: Record<string, unknown>): VoteInput | null {
  const placeKey = typeof v.place === "string" ? v.place : typeof v.placeKey === "string" ? v.placeKey : "";
  const itemId = typeof v.item === "string" ? v.item : typeof v.itemId === "string" ? v.itemId : "";
  const kind = v.kind, value = typeof v.v === "string" ? v.v : v.value;
  if (!PLACE_KEY.test(placeKey) || !VOICE_ID.test(itemId)) return null;
  if (!VOTE_KINDS.includes(kind as VoteKind) || !VOTE_VALUES.includes(value as VoteValue)) return null;
  return { placeKey, itemId, kind: kind as VoteKind, value: value as VoteValue };
}

/** The app's address for one answer (it signs the person in first; the app's screen sends it once and says thanks). */
export const VOTE_PATH = "/app/vote";
export function voteHref(o: VoteInput): string {
  const base = SIGNUP_BASE.slice(0, SIGNUP_BASE.indexOf("/app/"));
  return `${base}${VOTE_PATH}?place=${encodeURIComponent(o.placeKey)}&item=${encodeURIComponent(o.itemId)}&kind=${o.kind}&v=${o.value}`;
}

export const VOTE_WORDS = {
  thanksH: "Thanks. We kept your answer.",
  thanksUseful: "It counts toward the line under that item.",
  thanksTrue: "Only the place's team and ours read it.",
  back: "Back to the page",
  bad: "That link isn't complete. Go back to the page and tap again.",
  gone: "We can't find that item anymore. The record may have changed.",
  limited: "That's a lot of answers in an hour. Try again later.",
  off: "Answers aren't open yet.",
} as const;
